import { z } from 'zod';
import { GameId } from './game.ts';
import { GameSelection, MarketId, Odds } from './market.ts';
import { CurrencyCode, Money } from './money.ts';
import { BetId, PromotionId, Sha256Hex, UtcInstant } from './primitives.ts';

export const PromotionKind = z.enum(['LIGHTNING', 'FREEBET', 'BOOST', 'STREAK', 'LEVEL', 'DAILY_CHECKIN', 'COMEBACK']);
export type PromotionKind = z.infer<typeof PromotionKind>;

/**
 * Inventory item lifecycle. GRANTED → RESERVED (bet in flight) → CONSUMED (bet accepted);
 * RESERVED → GRANTED when the bet fails; CONSUMED → GRANTED/EXPIRED when the round is voided (policy);
 * GRANTED → EXPIRED | CANCELLED.
 */
export const PromotionState = z.enum(['GRANTED', 'RESERVED', 'CONSUMED', 'CANCELLED', 'EXPIRED']);
export type PromotionState = z.infer<typeof PromotionState>;

/** STAKE_INCLUDED: a win returns the full gross. PROFIT_ONLY: a win returns gross minus face value. */
export const FreebetPolicy = z.enum(['STAKE_INCLUDED', 'PROFIT_ONLY']);
export type FreebetPolicy = z.infer<typeof FreebetPolicy>;

export const RewardOrigin = z.enum(['OPERATOR_GRANT', 'DAILY_CHECKIN', 'LEVEL_UP', 'LEADERBOARD', 'DROP', 'CAMPAIGN']);

/** A player-owned inventory item. Non-money items are never presented as wallet balance. */
export const Promotion = z.object({
  promotionId: PromotionId,
  kind: z.enum(['FREEBET', 'BOOST']),
  state: PromotionState,
  origin: RewardOrigin,
  /** Idempotency key of the grant (operatorRewardId, check-in day, level). */
  grantKey: z.string().min(1).max(128),
  policyVersion: z.string().min(1),
  /** Freebet face value; the cash debit for a freebet stake is zero. */
  faceValue: Money.nullable(),
  freebetPolicy: FreebetPolicy.nullable(),
  /** Boost on the payout formula's (10000 + bps) term. */
  boostBps: z.number().int().positive().nullable(),
  games: z.array(GameId).min(1),
  currency: CurrencyCode,
  grantedAt: UtcInstant,
  expiresAt: UtcInstant.nullable(),
  /** Bet currently holding (RESERVED) or that used (CONSUMED) this item. */
  betId: BetId.nullable(),
  /** Real-money use is off by default and requires a separate review. */
  realMoneyEnabled: z.literal(false),
});
export type Promotion = z.infer<typeof Promotion>;

/** Operator-side grant (POST /v1/rewards). Idempotent by operatorRewardId. */
export const RewardGrantCommand = z.object({
  operatorRewardId: z.string().min(1).max(128),
  kind: z.enum(['FREEBET', 'BOOST']),
  faceValue: Money.nullable(),
  freebetPolicy: FreebetPolicy.nullable(),
  boostBps: z.number().int().positive().max(10000).nullable(),
  games: z.array(GameId).min(1),
  expiresAt: UtcInstant.nullable(),
});
export type RewardGrantCommand = z.infer<typeof RewardGrantCommand>;

/**
 * One formula and version for every promotional payout (docs §11):
 * gross = floor(stake × oddsN × lightningN × (10000 + boostBps) / (oddsD × lightningD × 10000)); payout = min(gross, cap).
 */
export const PromoMathPolicy = z.object({
  policyVersion: z.string().min(1),
  formula: z.literal('floor(stake*oN*lN*(10000+bps)/(oD*lD*10000)); min(gross,cap)'),
  /** Disclosed maximum total return per bet, in the wallet currency of the session. */
  payoutCap: Money,
  maxBoostBpsPerBet: z.number().int().nonnegative(),
  /** At most this many inventory items on one bet. */
  maxRewardsPerBet: z.number().int().nonnegative(),
  freebetWithLightning: z.boolean(),
  boostWithLightning: z.boolean(),
  /** What a void does to an item used on the voided bet. */
  voidPolicy: z.enum(['RESTORE_IF_NOT_EXPIRED']),
  realMoneyEnabled: z.literal(false),
});
export type PromoMathPolicy = z.infer<typeof PromoMathPolicy>;

export const LightningTier = z.enum(['COMMON', 'RARE', 'EPIC', 'LEGENDARY']);
export type LightningTier = z.infer<typeof LightningTier>;

/** Published before any bet: tier probabilities (weights / total), factors and the eligible pool. */
export const LightningPolicy = z.object({
  policyVersion: z.string().min(1),
  tiers: z.array(z.object({ tier: LightningTier, weight: z.number().int().positive(), factor: Odds })).min(1),
  /** Markets whose selections can be boosted. Whether the exact side market is included is explicit. */
  eligibleMarkets: z.array(MarketId).min(1),
  selectionsPerRound: z.number().int().positive(),
});
export type LightningPolicy = z.infer<typeof LightningPolicy>;

/** Locked in the round manifest when betting opens. The seed behind it is revealed after the round. */
export const LightningOffer = z.object({
  policyVersion: z.string().min(1),
  /** sha256(serverSeed). */
  commitment: Sha256Hex,
  boosts: z.array(z.object({ selection: GameSelection, tier: LightningTier, factor: Odds })),
});
export type LightningOffer = z.infer<typeof LightningOffer>;

/** Display-only conversion rate: 1 unit of `base` = numerator/denominator units of `quote`. */
export const FxQuote = z.object({
  rateId: z.string().min(1).max(64),
  provider: z.string().min(1).max(64),
  base: CurrencyCode,
  quote: CurrencyCode,
  rate: Odds,
  asOf: UtcInstant,
  ttlMs: z.number().int().positive(),
  /** true when the rate is an illustrative example and not market data. */
  exampleOnly: z.boolean(),
});
export type FxQuote = z.infer<typeof FxQuote>;

export const LevelState = z.object({
  level: z.number().int().positive(),
  xp: z.number().int().nonnegative(),
  xpForNext: z.number().int().positive(),
  policyVersion: z.string().min(1),
});
export type LevelState = z.infer<typeof LevelState>;

export const CheckinState = z.object({
  /** UTC day (YYYY-MM-DD) of the last claim. */
  lastClaimDay: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable(),
  /** Consecutive check-in days, not wins or losses. */
  streakDays: z.number().int().nonnegative(),
  canClaim: z.boolean(),
  nextClaimAt: UtcInstant.nullable(),
});
export type CheckinState = z.infer<typeof CheckinState>;

/** Operator-configured promotion features. Everything real-money is off. */
export const PromoFeatureFlags = z.object({
  inventory: z.boolean(),
  freebets: z.boolean(),
  boosts: z.boolean(),
  lightning: z.boolean(),
  level: z.boolean(),
  dailyCheckin: z.boolean(),
  /** Configurable slots; off unless an operator enables them after review. */
  streak: z.boolean(),
  comeback: z.boolean(),
  realMoney: z.literal(false),
});
export type PromoFeatureFlags = z.infer<typeof PromoFeatureFlags>;
